import { useState } from 'react'
import type { FC, ReactNode } from 'react'
import { Box, Button, Divider, Paper, Skeleton, Typography } from '@mui/material'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import NorthEastIcon from '@mui/icons-material/NorthEast'
import type { BffCrmPersonAdditionalBenefitsRetrieveResponse } from '@fireflyhealth/api'

import { EmptyState, SegmentedControl } from '~/shared/ui'
import type { PatientUser } from '~/shared/types'
import { useGetPersonAdditionalBenefits } from './PersonAdditionalBenefitsService'
import { PlanYearPicker } from '~/entities/health-plan'

// Server-owned shape — reuse the generated response type rather than re-declaring it.
type AdditionalBenefit = BffCrmPersonAdditionalBenefitsRetrieveResponse['firefly_benefits'][number]

type Segment = 'firefly' | 'employer'

interface AdditionalBenefitsProps {
  patient: PatientUser
}

/** Height of the logo well, so every card in a row lines its divider up at the same place. */
const LOGO_WELL_HEIGHT = 64

/**
 * One partner card: a logo well, a divider, the description, and the outbound link.
 *
 * Every field on the row is nullable, so the well doubles as the card's identity — it holds the
 * logo when there is one and the partner's name when there isn't, which keeps a partner with
 * neither logo nor description from rendering as an empty box. The logo carries the name as its
 * `alt` for the same reason: the URL is presigned and time-limited, so a card left open past expiry
 * falls back to the name rather than a broken image with nothing beside it.
 */
const BenefitCard: FC<{ benefit: AdditionalBenefit }> = ({ benefit }) => {
  const { partner_name: partnerName, description, logo, url } = benefit

  return (
    <Paper
      variant="outlined"
      data-testid={`additional-benefit-${partnerName}`}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        p: 2.5,
        borderRadius: '12px',
      }}
    >
      <Box
        sx={theme => ({
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: LOGO_WELL_HEIGHT,
          px: 2,
          borderRadius: '8px',
          bgcolor: theme.palette.tones.neutral.bg,
        })}
      >
        {logo ? (
          // `contain` inside a fixed-height well lets partner logos of any aspect ratio share one
          // card height without the client measuring them, which is what the app does instead.
          <Box
            component="img"
            src={logo}
            alt={partnerName ?? ''}
            sx={{ maxHeight: '100%', maxWidth: '100%', objectFit: 'contain' }}
          />
        ) : (
          <Typography
            sx={theme => ({
              fontSize: '1.4rem',
              fontWeight: 600,
              color: theme.palette.tones.neutral.fg,
              textAlign: 'center',
            })}
          >
            {partnerName}
          </Typography>
        )}
      </Box>

      <Divider />

      {description && (
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {description}
        </Typography>
      )}

      {url && (
        <Button
          component="a"
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          variant="outlined"
          size="small"
          endIcon={<NorthEastIcon />}
          data-testid={`additional-benefit-link-${partnerName}`}
          sx={{ mt: 'auto', alignSelf: 'flex-start' }}
        >
          Learn more
        </Button>
      )}
    </Paper>
  )
}

/** Two-up card grid, collapsing to one column in a narrow patient-detail panel. */
const BenefitGrid: FC<{ children: ReactNode }> = ({ children }) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
      gap: 2.5,
    }}
  >
    {children}
  </Box>
)

/** Placeholder cards for the initial fetch, sized like the real cards so the grid doesn't shift. */
const AdditionalBenefitsSkeleton: FC = () => (
  <Box data-testid="additional-benefits-loading">
    <BenefitGrid>
      {Array.from({ length: 4 }).map((_, index) => (
        <Paper key={index} variant="outlined" sx={{ p: 2.5, borderRadius: '12px' }}>
          <Skeleton variant="rounded" height={LOGO_WELL_HEIGHT} />
          <Skeleton variant="text" sx={{ mt: 2 }} />
          <Skeleton variant="text" width="70%" />
          <Skeleton
            variant="rounded"
            width={120}
            height={32}
            sx={{ mt: 2, borderRadius: '999px' }}
          />
        </Paper>
      ))}
    </BenefitGrid>
  </Box>
)

const AdditionalBenefitsTab: FC<{ personId: number | undefined }> = ({ personId }) => {
  // Unset until staff pick a year: the response names the one it answered from.
  const [pickedConfigurationId, setPickedConfigurationId] = useState<number>()
  // `null` until the user picks a segment, so the default can be derived from the payload below
  // rather than latched here — on the first render the query is still loading and both lists are
  // empty, which would strand any data-dependent initial value.
  const [segment, setSegment] = useState<Segment | null>(null)

  const {
    data,
    isPending: isLoading,
    isPlaceholderData,
    error,
  } = useGetPersonAdditionalBenefits({
    personId,
    policyConfigurationId: pickedConfigurationId,
  })

  const configurations = data?.policy_configurations ?? []

  if (isLoading) {
    return (
      <Box sx={{ p: 3 }}>
        <AdditionalBenefitsSkeleton />
      </Box>
    )
  }

  if (error) {
    return (
      <EmptyState
        icon={<InfoOutlinedIcon />}
        title="Couldn't load additional benefits"
        caption="Something went wrong fetching this member's additional benefits. Please try again."
      />
    )
  }

  if (configurations.length === 0) {
    return (
      <EmptyState
        icon={<InfoOutlinedIcon />}
        title="No additional benefits"
        caption="This member has no additional benefits to display, either because no plan configuration was found or their plan includes no point solutions."
      />
    )
  }

  const fireflyBenefits = data?.firefly_benefits ?? []
  const employerBenefits = data?.employer_benefits ?? []
  // The segments are shown only when the member has employer benefits in the selected year,
  // matching what their own app does. That also keeps the selection from stranding on a segment
  // the year being read does not offer.
  const showSegments = employerBenefits.length > 0
  // Open on a segment that has something in it. A member with employer benefits but none from
  // Firefly would otherwise land on an empty Firefly segment and have to find the control to see
  // anything, even though the year as a whole is not empty. An explicit choice still wins, so the
  // user can select the empty segment and see it.
  const defaultSegment: Segment = fireflyBenefits.length > 0 ? 'firefly' : 'employer'
  const activeSegment: Segment = showSegments ? (segment ?? defaultSegment) : 'firefly'
  const benefits = activeSegment === 'firefly' ? fireflyBenefits : employerBenefits
  // Both labels and both intros come from the response, so this tab words the segments exactly as
  // the member's app does and holds no copy of its own.
  const intro = (activeSegment === 'firefly' ? data?.firefly_intro : data?.employer_intro) ?? []

  // `keepPreviousData` leaves `data` on the year just left while a newly picked one loads, so
  // everything describing the year on screen waits for the new response rather than captioning a
  // loading grid with the old year's segments and copy.
  const isShowingSelectedYear = !isPlaceholderData

  const renderBenefits = () => {
    if (!isShowingSelectedYear) return <AdditionalBenefitsSkeleton />
    if (benefits.length === 0) {
      return (
        <EmptyState
          icon={<InfoOutlinedIcon />}
          title="No additional benefits"
          caption="This plan year includes no point solutions for this member."
        />
      )
    }
    return (
      <BenefitGrid>
        {benefits.map((benefit, index) => (
          // The payload carries no id and partner names are not guaranteed to be set, so the
          // server-fixed position in the list is the only stable key available.
          <BenefitCard key={`${benefit.partner_name}-${index}`} benefit={benefit} />
        ))}
      </BenefitGrid>
    )
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* The year on the left, the segments opposite it, as the design places them. */}
      <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 2, mb: 2.5 }}>
        <PlanYearPicker
          planYears={configurations}
          selectedPlanYearId={
            pickedConfigurationId ?? data?.selected_policy_configuration_id ?? undefined
          }
          onChange={setPickedConfigurationId}
          idPrefix="additional-benefits"
        />

        {showSegments && isShowingSelectedYear && (
          <Box sx={{ ml: 'auto', pb: 0.5 }}>
            <SegmentedControl
              aria-label="Additional benefits source"
              options={[
                { value: 'firefly', label: data?.firefly_label ?? '' },
                { value: 'employer', label: data?.employer_label ?? '' },
              ]}
              value={activeSegment}
              onChange={setSegment}
            />
          </Box>
        )}
      </Box>

      {isShowingSelectedYear && (
        <Box sx={{ mb: 2.5 }}>
          {intro.map((paragraph, index) => (
            // Same reasoning as the cards below: the server sends a plain string list with nothing
            // unique in it, so position is the only stable key. Two identical paragraphs would
            // collide on the text alone.
            <Typography
              key={`${paragraph}-${index}`}
              variant="body2"
              sx={{ color: 'text.secondary', mt: 0.5 }}
            >
              {paragraph}
            </Typography>
          ))}
        </Box>
      )}

      {renderBenefits()}
    </Box>
  )
}

// Keyed remount: the route is keyed by path, so a kept response or picked year would be stale.
const AdditionalBenefits: FC<AdditionalBenefitsProps> = ({ patient }) => (
  <AdditionalBenefitsTab key={patient.person?.id} personId={patient.person?.id} />
)

export default AdditionalBenefits
